import { lineString } from '@turf/helpers';
import simplify from '@turf/simplify';
import { ParentComponent, createSignal, mergeProps } from 'solid-js';
import { SETTINGS } from '../../settings';
import { Polyline, Vec2 } from '../../types';
import { cacheResponse } from '../../utils/cache';
import { Icon } from '../Icon';
import { Image, ImageProps } from '../Image';
import { InView } from '../InView';
import { themeClass, useTheme } from '../ThemeProvider';
import styles from './StaticMap.module.scss';

type SimpleImageProps = Omit<ImageProps, 'src' | 'alt' | 'width' | 'height'>;

export const MAX_MAP_WIDTH = 1280; // NOTE: This is set by the Mapbox API.
const MAX_LINE_LENGTH = 35;
const MAX_SRC_LENGTH = 7800;
const DEFAULT_ASPECT_RATIO = '16:9';
const MIN_SIMPLIFY_TOLERANCE = 0.0005;
const MAX_SIMPLIFY_TOLERANCE = 0.05;
const SIMPLIFY_SEARCH_ITERATIONS = 15;
const MAX_SRC_CACHE_SIZE = 500;

// NOTE: Finding the least simplified polyline that fits in the url is expensive (up to 16 simplify passes), so cache
// the result. Keyed by the full url params + polyline so any change in size, theme, color etc. is a cache miss.
const simplifiedSrcCache = new Map<string, string>();
const cacheSimplifiedSrc = (key: string, src: string) => {
  if (simplifiedSrcCache.size >= MAX_SRC_CACHE_SIZE) {
    const oldestKey = simplifiedSrcCache.keys().next().value;
    if (oldestKey !== undefined) simplifiedSrcCache.delete(oldestKey);
  }
  simplifiedSrcCache.set(key, src);
};

enum IMAGE_ASPECT_RATIOS {
  '16:9' = 56,
  '4:3' = 75,
  '1:1' = 100,
}

interface Props extends SimpleImageProps {
  location?: Vec2;
  overlay?: string;
  polyline?: Polyline;
  polylineColor?: string;
  polylinePins?: boolean;
  zoom?: number;
  abortMessage?: string;
  onLoad?: () => void;
}

export const StaticMap: ParentComponent<Props> = passedProps => {
  const props = mergeProps(
    {
      zoom: 10,
      location: [-1.4701, 53.3811],
      polylineColor: 'red',
      polylinePins: true,
      abortMessage: 'Sorry this ride / route has too many points to draw on a static map.',
    },
    passedProps,
  );
  const aspectRatio = passedProps.aspectRatio || DEFAULT_ASPECT_RATIO;

  const [theme] = useTheme();
  const { mode, settings } = theme();
  const [abortMap, setAbortMap] = createSignal(false);
  const [src, setSrc] = createSignal('');
  let mapContainerRef: HTMLDivElement | undefined;

  // NOTE: Build the src when the map is first seen rather than on mount. Generating it is expensive and maps that
  // aren't rendered yet (e.g. hidden ride cards) have no width, which would produce the wrong image size.
  const onSeen = () => {
    loadImage();
  };

  const loadImage = async () => {
    const cache = await caches.open(SETTINGS.IMAGE_CACHE_NAME);
    const src = getSrc();
    if (cache) {
      const cachedResp = await cache.match(src);
      if (cachedResp && cachedResp.ok) {
        const blob = await cachedResp.blob();
        const objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } else {
        setSrc(src);
      }
    } else {
      setSrc(src);
    }
  };

  const onImageLoad = (img: HTMLImageElement | undefined) => {
    if (img && img.src) {
      cacheImage(img.src);
    }
  };

  const cacheImage = async (src: string) => {
    const cache = await caches.open(SETTINGS.IMAGE_CACHE_NAME);
    if (!cache) return;
    const cachedResp = await cache.match(src);
    if (cachedResp && cachedResp.ok) return;
    if (src.startsWith('data:image') || src.startsWith('blob:')) return;
    fetch(src)
      .then(response => {
        cacheResponse(cache, src, response);
      })
      .catch(error => {
        console.error('Error caching image:', error);
      });
  };

  const simplifyPolyline = (line: Polyline, tolerance: number): Polyline => {
    if (!line || line.length < 2) {
      return line || [];
    }
    const simplified = simplify(lineString(line), {
      tolerance,
      highQuality: true,
    });
    if (simplified.geometry) {
      return simplified.geometry.coordinates.map(pos => [pos[0], pos[1]]);
    } else {
      return line;
    }
  };

  const buildPolylineOverlay = (tolerance: number) => {
    let overlay = '';
    if (props.polyline && props.polyline.length > 0) {
      let line = props.polyline;
      if (tolerance > 0 && line.length > MAX_LINE_LENGTH) {
        line = simplifyPolyline(line, tolerance);
      }
      const geoJSON = {
        type: 'Feature',
        properties: { stroke: props.polylineColor },
        geometry: {
          type: 'LineString',
          coordinates: line,
        },
      };
      overlay = `geojson(${JSON.stringify(geoJSON)})`;
      if (props.polylinePins && props.polyline.length > 0) {
        const start = props.polyline[0];
        const end = props.polyline[props.polyline.length - 1];
        overlay += `,pin-l-a+000000(${start[0]},${start[1]})`;
        overlay += `,pin-l-b+000000(${end[0]},${end[1]})`;
      }
      overlay = encodeURI(overlay).replace(/#/g, '%23');
      return overlay;
    }
    return overlay;
  };

  const getMapSize = () => {
    const maxWidth = MAX_MAP_WIDTH;
    let width = maxWidth;
    if (mapContainerRef && mapContainerRef.clientWidth > 0) {
      width = mapContainerRef.clientWidth;
    }
    width = Math.min(width, maxWidth);

    const ratio = IMAGE_ASPECT_RATIOS[aspectRatio] / 100;
    const height = width * ratio;

    return {
      width: Math.round(width),
      height: Math.round(height),
    };
  };

  const getSrc = () => {
    const base = 'https://api.mapbox.com/styles/v1/headwindapp';
    const mapStyle = mode === 'dark' ? settings.MAPBOX_THEME_DARK : settings.MAPBOX_THEME_LIGHT;
    const params = props.overlay || props.polyline ? 'auto' : `${props.location.join(',')},${props.zoom}`;

    // double image size on retina screens
    let retina = '';
    if (window.devicePixelRatio && window.devicePixelRatio > 1) {
      retina = '@2x';
    }

    const size = getMapSize();

    const constructUrl = (overlayPart: string) => {
      const overlayPath = overlayPart ? `${overlayPart}/` : '';
      return `${base}/${mapStyle}/static/${overlayPath}${params}/${size.width}x${size.height}${retina}?access_token=${settings.MAPBOX_API_KEY}`;
    };

    let src = '';

    if (props.polyline && props.polyline.length > 0) {
      if (props.polyline.length <= MAX_LINE_LENGTH) {
        src = constructUrl(buildPolylineOverlay(0));
      } else {
        const cacheKey = `${constructUrl('')}|${props.polylineColor}|${props.polylinePins}|${JSON.stringify(
          props.polyline,
        )}`;
        const cachedSrc = simplifiedSrcCache.get(cacheKey);
        if (cachedSrc) {
          src = cachedSrc;
        } else {
          // Start with high-quality tolerance
          src = constructUrl(buildPolylineOverlay(MIN_SIMPLIFY_TOLERANCE));

          // If the URL length exceeds MAX_SRC_LENGTH, adaptively increase tolerance
          // to find the least simplified polyline that fits within the limit
          if (src.length > MAX_SRC_LENGTH) {
            let low = MIN_SIMPLIFY_TOLERANCE;
            let high = MAX_SIMPLIFY_TOLERANCE;
            let bestSrc = '';

            for (let i = 0; i < SIMPLIFY_SEARCH_ITERATIONS; i++) {
              const mid = (low + high) / 2;
              const testSrc = constructUrl(buildPolylineOverlay(mid));
              if (testSrc.length <= MAX_SRC_LENGTH) {
                bestSrc = testSrc;
                high = mid; // Try smaller tolerance to keep more detail
              } else {
                low = mid; // Too long, need larger tolerance
              }
            }

            if (bestSrc) {
              src = bestSrc;
            }
          }
          cacheSimplifiedSrc(cacheKey, src);
        }
      }
    } else if (props.overlay) {
      src = constructUrl(props.overlay);
    } else {
      src = constructUrl('');
    }

    // if we reach the max url char length just show a fallback image and abandon the map
    if (src.length > MAX_SRC_LENGTH || !src) {
      setAbortMap(true);
      // NOTE: Below is a base 64 encoded blank 1x1 image, returning this causes the lazy load of the image to trigger and maintains proper
      // image size based on aspect ratio.
      return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
    }

    setAbortMap(false);
    return src;
  };

  return (
    <div ref={mapContainerRef} class={styles.container}>
      {abortMap() && (
        <div class={themeClass(styles.dark, styles.abortMap)}>
          <Icon type="IconAlertTriangle" size="xlarge" />
          <p>{props.abortMessage}</p>
        </div>
      )}
      <InView onSeen={onSeen}>
        <Image
          {...props}
          aspectRatio={aspectRatio}
          lazyload
          alt={props.overlay ? 'Static Map' : `Map of ${props.location.join(',')}`}
          src={src()}
          width="100%"
          onLoad={onImageLoad}
        />
      </InView>
    </div>
  );
};
